import { useAuth } from '@/app/providers/AuthProvider';

/**
 * Permission checking hook.
 *
 * Uses `permissionKeys` (string array) returned directly by the backend
 * instead of resolving numeric codes on the frontend.
 * This means the frontend never needs to maintain its own code→key mapping
 * for runtime permission checks — the backend is the single source of truth.
 *
 * The `permissions` (numeric) array is kept for backwards-compat, but all
 * checks here now use the string keys.
 */
export const usePermissions = () => {
    const { user } = useAuth();

    const permissionKeys: string[] = user?.access?.permissionKeys || [];
    const isOwner = user?.access?.isOwner || false;
    const isSuperAdmin = user?.access?.isSuperAdmin || false;

    /**
     * Check if the user has a specific permission by its string key.
     * e.g. hasPermission('flow_builder.view')
     */
    const hasPermission = (permission: string | number): boolean => {
        if (isOwner || isSuperAdmin) return true;
        if (typeof permission === 'number') {
            // Numeric check still supported as fallback (e.g. direct code usage)
            return (user?.access?.permissions || []).includes(permission);
        }
        return permissionKeys.includes(permission);
    };

    /**
     * Returns true if the user has at least one of the given permissions.
     */
    const hasAnyPermission = (requiredPermissions: (string | number)[]): boolean => {
        if (isOwner || isSuperAdmin) return true;
        return requiredPermissions.some(p => hasPermission(p));
    };

    /**
     * Returns true if the user has ALL of the given permissions.
     */
    const hasAllPermissions = (requiredPermissions: (string | number)[]): boolean => {
        if (isOwner || isSuperAdmin) return true;
        return requiredPermissions.every(p => hasPermission(p));
    };

    return {
        permissions: user?.access?.permissions || [],
        permissionKeys,
        isOwner,
        isSuperAdmin,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
    };
};
